import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./viewerWrapper.scss";
import { useParams, useLocation } from "react-router-dom";
import { Button, Popconfirm, Space, Tooltip } from "antd";
import { CloseOutlined, FormOutlined, SyncOutlined } from "@ant-design/icons";
import { ReactComponent as RadiolinqLogo } from "../../../assets/images/White Logo.svg";
import ReportForm from "../ReportForm";
import { CaseDetail } from "../../../models/CaseDetail/caseDetail.model";
import { UserRoleEnum } from "../../../enums/userRole.enum";
import AdminCaseService from "../../../services/Case/AdminCase/adminCase.service";
import DoctorCaseService from "../../../services/Case/DoctorCase/doctorCase.service";
import AuthContainer from "../../../store/container/AuthContainer";
import { AuthReducerProps } from "../../../store/reducers/authReducer";
import { CaseStatusEnum } from "../../../enums/caseStatus.enum";
import CryptoJS from "crypto-js";
import { useHistory } from "react-router-dom";
import ScanCenterCaseService from "../../../services/Case/ScanCenterCase/scanCenterCase.service";
import ViewerIframe from "../ViewerIframe";
import ProfileService from "../../../services/Profile/profile.service";
import { Admin } from "../../../models/Admin/admin.model";
import { Doctor } from "../../../models/Doctor/doctor.model";
import { ScanCenter } from "../../../models/ScanCenter/scanCenter.model";
import { AdminFeatureEnum } from "../../../enums/AdminFeature.enum";
import { AppRoutes } from "../../../routes/routeConstants/appRoutes";
import NotesPanel from "../NotesPanel";
import {
  ViewerNotesProvider,
  useViewerNotes,
} from "../../context/ViewerNotesContext";
import { DoctorFeatureEnum } from "../../../enums/DoctorFeature.enum";
import { getCosignConfirm } from "../../utils/cosignConfirm";

interface ViewerWrapperProps extends AuthReducerProps {}

const REPORT_PANEL_WIDTH_KEY = "viewerReportPanelWidthPct";
const DEFAULT_PANEL_PCT = 50;
const MIN_PANEL_PCT = 20;
const MAX_PANEL_PCT = 88;

function clampPct(n: number): number {
  return Math.min(MAX_PANEL_PCT, Math.max(MIN_PANEL_PCT, n));
}

function readStoredPct(): number {
  try {
    const raw = localStorage.getItem(REPORT_PANEL_WIDTH_KEY);
    if (!raw) return DEFAULT_PANEL_PCT;
    const v = parseFloat(raw);
    return Number.isFinite(v) ? clampPct(v) : DEFAULT_PANEL_PCT;
  } catch {
    return DEFAULT_PANEL_PCT;
  }
}

function ViewerWrapperBody({ userRole, user }: ViewerWrapperProps) {
  const history = useHistory<any>();
  const notesCtx = useViewerNotes();

  const { caseId }: any = useParams();
  const location = useLocation();
  const seriesId = new URLSearchParams(location.search).get("seriesId") ?? undefined;

  const viewerListReturn = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const p = sp.get("viewerListPath");
    const s = sp.get("viewerListSearch");
    return {
      viewerListPath: p ?? undefined,
      viewerListSearch: s ?? undefined,
    };
  }, [location.search]);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelWidthPct, setPanelWidthPct] = useState(readStoredPct);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );

  const [me, setMe] = useState<Admin | Doctor | ScanCenter>();
  const [caseDetail, setCaseDetail] = useState<CaseDetail>();
  const [authCode, setAuthCode] = useState<string>();
  const [lmsBarVisible, setLmsBarVisible] = useState(true);
  const [clinicalHistoryToolbarVisible, setClinicalHistoryToolbarVisible] = useState(true);
  const [viewerReloadKey, setViewerReloadKey] = useState(0);

  const url = process.env.REACT_APP_VIEWER_BASE_URL ?? "";
  const hasDownloadZip = user?.adminFeatures?.some(
    (f) => f.feature === AdminFeatureEnum.EXPORT_IMAGE_ZIP
  );

  // Stable refs — mutated during drag, no re-renders
  const panelWidthPctRef = useRef(panelWidthPct);
  panelWidthPctRef.current = panelWidthPct;

  const handleRef       = useRef<HTMLDivElement>(null);
  const iframeWrapperRef = useRef<HTMLDivElement>(null);
  const viewerIframeRef = useRef<HTMLIFrameElement>(null);
  const reportPanelRef  = useRef<HTMLDivElement>(null);
  const rootRef         = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const stored = localStorage.getItem("auth");
    if (!stored) return;
    const auth = JSON.parse(stored);
    const encrypted = CryptoJS.AES.encrypt(JSON.stringify(auth), "LMUqneJBeU").toString();
    setAuthCode(encodeURIComponent(encrypted));
  }, []);

  // Hides fixed AppSidebar / AppHeader chrome via `.dicom-viewer-mode` (see
  // appSidebar.scss / appHeader.scss). Also added to <html> so global CSS
  // (see _main.scss) can pin the document to 100dvh + overflow:hidden while
  // the viewer is mounted — this is what stops iOS Safari's pull-to-refresh
  // from firing when the user drags inside the embedded viewer canvas.
  useEffect(() => {
    const appRoutesNode = document.getElementById("appRoutes");
    const htmlNode = document.documentElement;
    if (appRoutesNode) {
      appRoutesNode.classList.add("dicom-viewer-mode");
    }
    htmlNode.classList.add("dicom-viewer-mode");
    return () => {
      if (appRoutesNode) {
        appRoutesNode.classList.remove("dicom-viewer-mode");
      }
      htmlNode.classList.remove("dicom-viewer-mode");
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Refetch the current case so `caseDetail.status` stays in sync with the
  // server. Used on mount and again after a publish from the embedded
  // ReportForm, so the "Generate Report" button hides / the panel can close
  // once the case transitions to REPORTED (prevents the viewer from
  // re-POSTing publish_direct_report_upload against an already-published
  // report id).
  const refetchCaseDetail = useCallback(() => {
    if (!caseId || !userRole) return;
    const showCase =
      userRole === UserRoleEnum.ADMIN
        ? AdminCaseService.showAdminCase
        : userRole === UserRoleEnum.DOCTOR
        ? DoctorCaseService.showDoctorCase
        : ScanCenterCaseService.showScanCenterCase;
    showCase(Number(caseId), (d: CaseDetail) => setCaseDetail(d), () => {}, () => {});
  }, [caseId, userRole]);

  useEffect(() => {
    if (!caseId || !userRole) return;
    refetchCaseDetail();
    ProfileService.getProfile(userRole, (m: Admin | Doctor | ScanCenter) => setMe(m), () => {}, () => {});
  }, [caseId, userRole, refetchCaseDetail]);

  const handleReportPublish = useCallback(() => {
    refetchCaseDetail();
    setPanelOpen(false);
  }, [refetchCaseDetail]);

  useEffect(() => {
    setLmsBarVisible(true);
  }, [caseId]);

  // Browser tab title: show patient name + study so users can identify a
  // viewer tab at a glance without clicking through. Resets to the default
  // (from public/index.html) when this route unmounts.
  useEffect(() => {
    const defaultTitle = "Radiolinq - Imaging Anytime Anywhere";
    const patient = (caseDetail?.patientName ?? "").trim();
    const scan = (caseDetail?.scanTypeName ?? "").trim();
    const study = (caseDetail?.studyDescription ?? "").trim();
    const studyLabel = study && study !== "-" ? (scan ? `${scan} - ${study}` : study) : scan;
    const parts = [patient, studyLabel].filter(Boolean);
    document.title = parts.length > 0 ? `${parts.join(" · ")} | Radiolinq` : defaultTitle;
    return () => {
      document.title = defaultTitle;
    };
  }, [caseDetail?.patientName, caseDetail?.scanTypeName, caseDetail?.studyDescription]);

  // Listen for messages from the viewer iframe:
  //   - TOGGLE_LMS_BAR: show/hide the parent LMS bar
  //   - VIEWER_TOGGLE_FULLSCREEN: fullscreen the parent viewer shell (keeps LMS bar + FAB visible)
  //   - VIEWER_GO_TO_CASE_LIST: navigate to originating list page (path + query from viewerListPath / Search)
  const pendingViewerImageRef = useRef<string | null>(null);

  useEffect(() => {
    const handler = (e: MessageEvent) => {
      if (e.source !== viewerIframeRef.current?.contentWindow) return;
      if (e.data?.type === "TOGGLE_LMS_BAR") {
        if (typeof e.data.hidden !== "boolean") return;
        setLmsBarVisible(!e.data.hidden);
        return;
      }
      if (e.data?.type === "VIEWER_GO_TO_CASE_LIST") {
        const pathname =
          typeof e.data.path === "string" && e.data.path.startsWith("/")
            ? e.data.path
            : AppRoutes.CASES;
        const raw = e.data.search as string | undefined;
        const search =
          raw == null || raw === ""
            ? ""
            : raw.startsWith("?")
            ? raw
            : `?${raw}`;
        history.push({ pathname, search });
        return;
      }
      if (e.data?.type === "VIEWER_TOGGLE_FULLSCREEN") {
        const el = rootRef.current;
        if (!el) return;
        if (!document.fullscreenElement) {
          el.requestFullscreen?.().catch(() => {});
        } else {
          document.exitFullscreen?.().catch(() => {});
        }
      }
      if (e.data?.type === "VIEWER_COPY_IMAGE") {
        const dataUrl = e.data.dataUrl;
        if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image/")) return;
        // Store until the user explicitly presses Ctrl+V.
        pendingViewerImageRef.current = dataUrl;
        return;
      }
      if (e.data?.type === "VIEWER_PASTE_REQUEST") {
        // Viewer's Ctrl+V — paste the pending image into the report editor.
        const dataUrl = pendingViewerImageRef.current;
        if (!dataUrl) return;
        window.dispatchEvent(
          new CustomEvent("viewer-canvas-copy", { detail: { dataUrl } })
        );
        pendingViewerImageRef.current = null;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  // When the panel is open and the user presses Ctrl+V in the parent window
  // (after having clicked outside the iframe), forward any clipboard image to
  // the report editor via the same `viewer-canvas-copy` event.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (!panelOpen) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith("image/")) {
          const file = items[i].getAsFile();
          if (!file) continue;
          const reader = new FileReader();
          reader.onload = () => {
            const dataUrl = reader.result as string;
            window.dispatchEvent(
              new CustomEvent("viewer-canvas-copy", { detail: { dataUrl } })
            );
          };
          reader.readAsDataURL(file);
          e.preventDefault();
          break;
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [panelOpen]);

  // Mirror native fullscreen state back into the viewer iframe so its toolbar icon stays in sync
  useEffect(() => {
    const onFsChange = () => {
      const isFs = !!document.fullscreenElement;
      viewerIframeRef.current?.contentWindow?.postMessage(
        { type: "FULLSCREEN_STATE_CHANGED", isFullscreen: isFs },
        "*"
      );
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  const openPanel  = () => setPanelOpen(true);
  const closePanel = () => setPanelOpen(false);

  // ─── Direct DOM update — called on every pointer-move frame (zero re-renders) ─
  const applyDomSplit = useCallback((drawerPct: number) => {
    const iframePct = 100 - drawerPct;

    const iw = iframeWrapperRef.current;
    if (iw) iw.style.width = `${iframePct}vw`;

    const iframe = iw?.querySelector<HTMLIFrameElement>("iframe");
    if (iframe) iframe.style.width = `${iframePct}vw`;

    const panel = reportPanelRef.current;
    if (panel) panel.style.width = `${drawerPct}%`;

    const handle = handleRef.current;
    if (handle) handle.style.left = `${iframePct}vw`;
  }, []);

  // ─── Pointer-capture handlers ─────────────────────────────────────────────────
  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleRef.current?.setPointerCapture(e.pointerId);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    applyDomSplit(panelWidthPctRef.current); // seed DOM with current value
  }, [applyDomSplit]);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!handleRef.current?.hasPointerCapture(e.pointerId)) return;
    e.preventDefault();
    const pct = clampPct(((window.innerWidth - e.clientX) / window.innerWidth) * 100);
    panelWidthPctRef.current = pct;
    applyDomSplit(pct);
  }, [applyDomSplit]);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!handleRef.current?.hasPointerCapture(e.pointerId)) return;
    handleRef.current.releasePointerCapture(e.pointerId);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    const final = panelWidthPctRef.current;
    // One React re-render to sync the `style` prop on the report panel
    setPanelWidthPct(final);
    try { localStorage.setItem(REPORT_PANEL_WIDTH_KEY, String(final)); } catch { /* noop */ }
  }, []);

  // ─── Layout ───────────────────────────────────────────────────────────────────
  const splitActive = panelOpen && !isMobile;

  useEffect(() => {
    if (splitActive) return;
    const iw = iframeWrapperRef.current;
    if (iw) {
      iw.style.width = "";
      iw.style.left = "";
    }
    const iframe = viewerIframeRef.current;
    if (iframe) {
      iframe.style.width = "";
      iframe.style.left = "";
    }
  }, [splitActive]);
  const iframePct   = 100 - panelWidthPct;

  const iframeWrapperStyle: React.CSSProperties | undefined = splitActive
    ? { width: `${iframePct}vw`, left: 0 }
    : undefined;

  const iframeStyle: React.CSSProperties | undefined = splitActive
    ? { width: `${iframePct}vw`, left: 0 }
    : undefined;

  const panelStyle: React.CSSProperties = isMobile
    ? { width: "100%" }
    : { width: `${panelWidthPct}%` };

  const adminFeatures = me?.adminFeatures?.map((f) => f.feature) ?? [];

  // Full feature list (admin + doctor). Admin-side features live on
  // `adminFeatures` for admins; doctors get them via `doctorFeatures`.
  const doctorFeatures =
    (me as Doctor | undefined)?.doctorFeatures?.map((f: any) => f.feature) ?? [];
  const allFeatures: string[] = [...adminFeatures, ...doctorFeatures];
  const hasVoiceFeature = allFeatures.includes(AdminFeatureEnum.VOICE_TO_TEXT);
  const hasAugnitoFeature = allFeatures.includes("augnito_stt");

  const isCosignEnabled = adminFeatures.includes(AdminFeatureEnum.COSIGN);

  let showGenerateReport = false;
  if (isCosignEnabled) {
    // Doctors must not open Generate Report while case awaits admin approval; admins still can.
    if (userRole === UserRoleEnum.DOCTOR) {
      showGenerateReport =
        caseDetail?.status !== CaseStatusEnum.REPORTED &&
        caseDetail?.status !== CaseStatusEnum.TO_BE_APPROVED;
    } else {
      showGenerateReport = caseDetail?.status !== CaseStatusEnum.REPORTED;
    }
  } else if (userRole === UserRoleEnum.DOCTOR) {
    showGenerateReport = caseDetail?.status === CaseStatusEnum.ASSIGNED;
  } else {
    showGenerateReport =
      caseDetail?.status === CaseStatusEnum.TO_BE_APPROVED ||
      (caseDetail?.status === CaseStatusEnum.ASSIGNED && user?.id === caseDetail?.assignedToAdminId);
  }

  // Doctor-only gate: the NO_REPORT_EDIT feature locks a doctor out of
  // authoring/editing reports entirely (mirrors the listing — see
  // `canGenerateReport` in DoctorCases). Admins are unaffected.
  if (
    userRole === UserRoleEnum.DOCTOR &&
    doctorFeatures.includes(DoctorFeatureEnum.NO_REPORT_EDIT)
  ) {
    showGenerateReport = false;
  }

  // When cosign is in play, opening the panel triggers cosign_assign which
  // silently takes ownership of the case. Decide whether to confirm first.
  const isTypist = doctorFeatures.includes(DoctorFeatureEnum.TYPIST_LOGIN);
  const cosignConfirm = getCosignConfirm({
    caseDetail,
    userRole,
    userId: me?.id,
    isTypist,
    isCosignEnabled,
  });

  return (
    <div ref={rootRef} className={`viewer-wrapper ${panelOpen ? "report-panel-open" : ""}`}>
      {/* ── Row 1: LMS bar — logo, case list, refresh | clinical history, generate report ── */}
      <div className={`viewer-wrapper__lms-bar${lmsBarVisible ? "" : " viewer-wrapper__lms-bar--hidden"}`}>
        <div className="viewer-wrapper__lms-bar__left">
          <RadiolinqLogo className="viewer-wrapper__lms-bar__logo" />
          <span className="viewer-wrapper__lms-bar__brand-sep" />
          <Button
            type="text"
            size="small"
            icon={<SyncOutlined />}
            className="viewer-wrapper__lms-bar__back-btn viewer-wrapper__lms-bar__refresh-btn"
            title="Refresh viewer"
            onClick={() => setViewerReloadKey(k => k + 1)}
          >
            Refresh
          </Button>
        </div>

        <div className="viewer-wrapper__lms-bar__right">
          <div className="viewer-wrapper__lms-bar__history">
            {clinicalHistoryToolbarVisible ? (
              <Tooltip
                title={caseDetail?.parsedPatientHistory?.trim() ? caseDetail.parsedPatientHistory : undefined}
                overlayStyle={{ maxWidth: 500, whiteSpace: "pre-wrap" }}
                placement="bottom"
              >
                <div className="viewer-wrapper__clinical-history-panel">
                  <span className="viewer-wrapper__clinical-history-panel__title">
                    Clinical history
                  </span>
                  <Button
                    type="link"
                    size="small"
                    className="viewer-wrapper__clinical-history-panel__toggle"
                    onClick={() => setClinicalHistoryToolbarVisible(false)}
                  >
                    Hide
                  </Button>
                  <div className="viewer-wrapper__clinical-history-panel__text">
                    {caseDetail?.parsedPatientHistory?.trim() ? caseDetail.parsedPatientHistory : "—"}
                  </div>
                </div>
              </Tooltip>
            ) : (
              <Button
                type="text"
                size="small"
                className="viewer-wrapper__show-history-btn"
                onClick={() => setClinicalHistoryToolbarVisible(true)}
              >
                History
              </Button>
            )}
          </div>

          <Space size={8} wrap={false} align="center" className="viewer-wrapper__lms-bar__actions">
            {showGenerateReport && (
              cosignConfirm ? (
                <Popconfirm
                  title={cosignConfirm.message}
                  okText={cosignConfirm.okText}
                  cancelText="Cancel"
                  placement="bottomRight"
                  onConfirm={openPanel}
                >
                  <Button
                    type="primary"
                    size="small"
                    icon={<FormOutlined />}
                    className="viewer-wrapper__lms-bar__generate-btn"
                  >
                    Generate Report
                  </Button>
                </Popconfirm>
              ) : (
                <Button
                  type="primary"
                  size="small"
                  icon={<FormOutlined />}
                  className="viewer-wrapper__lms-bar__generate-btn"
                  onClick={openPanel}
                >
                  Generate Report
                </Button>
              )
            )}
          </Space>
        </div>
      </div>

      {/* ── Row 2: iframe fills remaining height ── */}
      <div
        ref={iframeWrapperRef}
        className="viewer-wrapper__iframe-wrapper"
        style={iframeWrapperStyle}
      >
        {authCode && (
          <ViewerIframe
            ref={viewerIframeRef}
            key={viewerReloadKey}
            loadAllSeries={adminFeatures.includes(AdminFeatureEnum.VIEWER_LOAD_ALL_SERIES)}
            caseId={caseId}
            url={url}
            encryptedAuth={authCode}
            seriesId={seriesId}
            hasDownloadZip={hasDownloadZip}
            viewerListPath={viewerListReturn.viewerListPath}
            viewerListSearch={viewerListReturn.viewerListSearch}
            frameStyle={iframeStyle}
          />
        )}
        {/* Floating quick-notes FAB + panel over the iframe. Hidden when report drawer is open. */}
        {!panelOpen && (
          <NotesPanel
            caseId={caseId}
            caseDetailId={caseDetail?.id}
            userRole={userRole}
            userId={user?.id}
            hasVoice={hasVoiceFeature}
            hasAugnito={hasAugnitoFeature}
            visible={showGenerateReport}
            onOpenReport={(text) => {
              if (text) notesCtx?.setPending(text);
              openPanel();
            }}
          />
        )}
      </div>

      {/* Drag handle — sits exactly on the seam, uses pointer capture */}
      {splitActive && (
        <div
          ref={handleRef}
          className="viewer-wrapper__resize-handle"
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize report panel"
          style={{ left: `${iframePct}vw` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
      )}

      {/* Report panel — plain fixed div, no Ant Design Drawer */}
      {panelOpen && (
        <div
          ref={reportPanelRef}
          className="viewer-wrapper__report-panel"
          style={panelStyle}
        >
          <button
            className="viewer-wrapper__report-panel-close"
            onClick={closePanel}
            aria-label="Close report panel"
          >
            <CloseOutlined />
          </button>
          {caseDetail && (
            <ReportForm
              fromViewer
              caseDetailId={caseDetail.id}
              onClose={closePanel}
              onPublish={handleReportPublish}
            />
          )}
        </div>
      )}
    </div>
  );
}

function ViewerWrapper(props: ViewerWrapperProps) {
  return (
    <ViewerNotesProvider>
      <ViewerWrapperBody {...props} />
    </ViewerNotesProvider>
  );
}

export default AuthContainer(ViewerWrapper);
